import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import menuService from '../services/menuService'
import orderService from '../services/orderService'
import { Menu, MenuItem, MenuDish } from '../types/menu'
import { useTranslation } from '../hooks/use-i18n'

interface AddOrderItemModalProps {
  restaurantId: string
  orderId: string
  onClose: () => void
  onItemAdded: () => void
}

interface OrderItemRow {
  key: string
  name: string
  price?: number
  isDish: boolean
  itemId?: string
  dishId?: string
}

const AddOrderItemModal: React.FC<AddOrderItemModalProps> = ({
  restaurantId,
  orderId,
  onClose,
  onItemAdded,
}) => {
  const { t } = useTranslation()
  const [menus, setMenus] = useState<Menu[]>([])
  const [selectedMenuId, setSelectedMenuId] = useState<string>('')
  const [menuDetail, setMenuDetail] = useState<Menu | null>(null)
  const [loadingMenus, setLoadingMenus] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [prices, setPrices] = useState<Record<string, number>>({})
  const [addingKey, setAddingKey] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMenus(true)
        const data = await menuService.getMenus(restaurantId)
        setMenus(data)
        if (data.length > 0) {
          setSelectedMenuId(data[0]._id || data[0].id || '')
        }
      } catch (err) {
        toast.error(t('toast.menusLoadFailed'))
      } finally {
        setLoadingMenus(false)
      }
    }
    load()
  }, [restaurantId])

  useEffect(() => {
    if (!selectedMenuId) {
      setMenuDetail(null)
      return
    }
    const load = async () => {
      try {
        setLoadingDetail(true)
        const detail = await menuService.getMenu(selectedMenuId)
        setMenuDetail(detail)
      } catch (err) {
        toast.error(t('toast.menuLoadFailed'))
      } finally {
        setLoadingDetail(false)
      }
    }
    load()
  }, [selectedMenuId])

  const isCustom = menuDetail?.type === 'custom' || (menuDetail?.dishes?.length || 0) > 0

  const rows: OrderItemRow[] = useMemo(() => {
    if (!menuDetail) return []
    if (isCustom) {
      return (menuDetail.dishes || []).map((d: MenuDish) => ({
        key: `dish-${d.id}`,
        name: d.name,
        isDish: true,
        dishId: d.id,
      }))
    }
    return (menuDetail.items || []).map((it: MenuItem) => {
      const id = (it._id || it.id) as string
      return {
        key: `item-${id}`,
        name: it.name,
        price: it.price,
        isDish: false,
        itemId: id,
      }
    })
  }, [menuDetail, isCustom])

  const getQty = (key: string) => quantities[key] ?? 1

  const setQty = (key: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [key]: Math.max(1, Math.min(999, value)) }))
  }

  const handleAdd = async (row: typeof rows[number]) => {
    try {
      setAddingKey(row.key)
      await orderService.addItem(restaurantId, orderId, {
        menuItemId: row.itemId,
        dishId: row.dishId,
        quantity: getQty(row.key),
        unitPrice: row.isDish ? prices[row.key] ?? 0 : undefined,
      })
      toast.success(t('toast.addedOrderItems', { quantity: String(getQty(row.key)), name: row.name }))
      onItemAdded()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.addOrderItemFailed'))
    } finally {
      setAddingKey(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="blue-sky-card rounded-3xl p-6 sm:p-8 w-full max-w-2xl mx-4 shadow-2xl animate-scale-in max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">{t('order.addModal.title')}</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl transition-colors"
          >
            ✕
          </button>
        </div>

        {loadingMenus ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
          </div>
        ) : menus.length === 0 ? (
          <div className="text-center py-10 text-white/60">
            {t('order.addModal.emptyMenus')}
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-white/90 mb-2">{t('order.addModal.menuLabel')}</label>
              <select
                value={selectedMenuId}
                onChange={(e) => setSelectedMenuId(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300"
              >
                {menus.map((m) => (
                  <option key={m._id || m.id} value={m._id || m.id} className="bg-[#2a2a2a]">
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto -mx-1 px-1">
              {loadingDetail ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                </div>
              ) : rows.length === 0 ? (
                <div className="text-center py-10 text-white/60">
                  {t('order.addModal.emptyItems')}
                </div>
              ) : (
                <div className="space-y-2">
                  {rows.map((row) => (
                    <div
                      key={row.key}
                      className="bg-[#1f1f1f] rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap"
                    >
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{row.name}</p>
                        {!row.isDish && (
                          <p className="text-white/50 text-xs">
                            {typeof row.price === 'number' ? t('menu.priceFormat', { amount: row.price.toFixed(2) }) : t('order.addModal.noPrice')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {row.isDish && (
                          <div className="flex items-center gap-1">
                            <span className="text-white/50 text-xs">$</span>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={prices[row.key] ?? 0}
                              onChange={(e) =>
                                setPrices((prev) => ({
                                  ...prev,
                                  [row.key]: Math.max(0, parseFloat(e.target.value) || 0),
                                }))
                              }
                              className="w-20 px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                            />
                          </div>
                        )}
                        <div className="flex items-center bg-white/10 border border-white/20 rounded-lg">
                          <button
                            type="button"
                            onClick={() => setQty(row.key, getQty(row.key) - 1)}
                            className="px-2.5 py-1.5 text-white/80 hover:text-white"
                          >
                            −
                          </button>
                          <span className="w-8 text-center text-white text-sm">{getQty(row.key)}</span>
                          <button
                            type="button"
                            onClick={() => setQty(row.key, getQty(row.key) + 1)}
                            className="px-2.5 py-1.5 text-white/80 hover:text-white"
                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAdd(row)}
                          disabled={addingKey === row.key}
                          className="px-3 py-1.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-lg text-sm font-semibold transition-all duration-300 disabled:opacity-50"
                        >
                          {addingKey === row.key ? t('order.addModal.adding') : t('order.addModal.add')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 mt-2 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="w-full px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 font-semibold"
              >
                {t('order.addModal.done')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AddOrderItemModal
